import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

const DEFAULT_GALLERY_BASE_URL = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'

export const supabase = createClient(supabaseUrl, supabaseKey)

function normalizeChurchName(value) {
  if (typeof value !== 'string') return ''
  return value.trim()
}

function normalizeConfig(config) {
  if (!config || typeof config !== 'object') {
    return {
      id: 1,
      church_name: '',
      gallery_base_url: DEFAULT_GALLERY_BASE_URL,
      delivery_time: '9:00 AM Easter Sunday',
    }
  }

  return {
    ...config,
    church_name: normalizeChurchName(config.church_name),
    gallery_base_url: config.gallery_base_url?.trim() || DEFAULT_GALLERY_BASE_URL,
    delivery_time: config.delivery_time?.trim() || '9:00 AM Easter Sunday',
  }
}

export function buildGalleryUrl(baseUrl, sessionId) {
  const root = typeof baseUrl === 'string' && baseUrl.trim() ? baseUrl.trim() : DEFAULT_GALLERY_BASE_URL
  return `${root.replace(/\/$/, '')}/session/${sessionId}`
}

function normalizeSession(session) {
  if (!session || typeof session !== 'object') return session

  return {
    ...session,
    church_name: normalizeChurchName(session.church_name),
  }
}

function normalizeSessionList(sessions) {
  if (!Array.isArray(sessions)) return sessions
  return sessions.map(normalizeSession)
}

// Session API
export async function createSession(sessionData) {
  const safeSessionData = {
    ...sessionData,
    church_name: normalizeChurchName(sessionData?.church_name),
  }

  console.log('Creating session with data:', safeSessionData)
  console.log('Supabase URL:', supabaseUrl)
  console.log('Supabase Key exists:', !!supabaseKey)

  const { data, error } = await supabase
    .from('sessions')
    .insert([safeSessionData])
    .select()
    .single()
  
  if (error) {
    console.error('Supabase insert error:', {
      message: error.message,
      code: error.code,
      status: error.status,
      details: error.details,
      hint: error.hint,
    })
    throw error
  }
  return normalizeSession(data)
}

export async function getSession(id) {
  const { data, error } = await supabase
    .from('sessions')
    .select('*, photos(*)')
    .eq('id', id)
    .single()
  
  if (error) throw error
  return normalizeSession(data)
}

export async function updateSession(id, updates) {
  const safeUpdates = {
    ...updates,
    church_name: updates?.church_name ? normalizeChurchName(updates.church_name) : updates?.church_name,
  }

  const { data, error } = await supabase
    .from('sessions')
    .update(safeUpdates)
    .eq('id', id)
    .select()
    .single()
  
  if (error) throw error
  return normalizeSession(data)
}

export async function listSessions() {
  const { data, error } = await supabase
    .from('sessions')
    .select('*, photos(*)')
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return normalizeSessionList(data)
}

// Photo API
export async function uploadPhoto(sessionId, blob) {
  const timestamp = Date.now()
  const path = `${sessionId}/${timestamp}.jpg`
  
  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from('photos')
    .upload(path, blob, {
      contentType: 'image/jpeg',
      upsert: false
    })
  
  if (uploadError) throw uploadError
  
  // Get public URL
  const { data: urlData } = supabase.storage
    .from('photos')
    .getPublicUrl(path)
  
  // Save to database
  const { data, error } = await supabase
    .from('photos')
    .insert([{
      session_id: sessionId,
      storage_path: path,
      public_url: urlData.publicUrl
    }])
    .select()
    .single()
  
  if (error) throw error
  return data
}

export async function deleteSession(sessionId, photos = []) {
  // Remove photos from storage
  const paths = photos.map(p => p.storage_path).filter(Boolean)
  if (paths.length > 0) {
    const { error: storageError } = await supabase.storage.from('photos').remove(paths)
    if (storageError) console.error('Storage delete error:', storageError)
  }
  // Remove photo rows
  const { error: photosError } = await supabase.from('photos').delete().eq('session_id', sessionId)
  if (photosError) console.error('Photos DB delete error:', photosError)
  // Remove session row
  const { error } = await supabase.from('sessions').delete().eq('id', sessionId)
  if (error) throw error
}

export async function deletePhoto(photoId, storagePath) {
  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from('photos')
    .remove([storagePath])
  
  if (storageError) console.error('Storage delete error:', storageError)
  
  // Delete from database
  const { error } = await supabase
    .from('photos')
    .delete()
    .eq('id', photoId)
  
  if (error) throw error
}

// Config API
export async function getConfig() {
  const { data, error } = await supabase
    .from('event_config')
    .select('*')
    .eq('id', 1)
    .maybeSingle()
  
  if (error) throw error
  return normalizeConfig(data)
}

// Quiz content API
export async function getQuizStages() {
  const { data, error } = await supabase
    .from('quiz_stages')
    .select('*')
    .order('stage_order', { ascending: true })

  if (error) throw error
  return data
}

// Session View Count API
export async function incrementSessionViewCount(sessionId) {
  const { error } = await supabase.rpc('increment_session_view_count', { session_id: sessionId })
  if (error) throw error
}

// Game Activity API
export async function recordGamePlay(gameType) {
  const { error } = await supabase
    .from('game_activity')
    .insert([{ game_type: gameType }])

  if (error) throw error
}

export async function getGamePlaysCount() {
  const { count, error } = await supabase
    .from('game_activity')
    .select('*', { count: 'exact', head: true })

  if (error) throw error
  return count ?? 0
}

export async function getBibleVerse(id = 1) {
  const { data, error } = await supabase
    .from('bible_verses')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data
}

export async function updateConfig(updates) {
  const safeUpdates = {
    id: 1,
    ...updates,
    church_name: normalizeChurchName(updates?.church_name),
  }

  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('updateConfig timed out')), 8000)
  )

  const dbPromise = supabase
    .from('event_config')
    .upsert(safeUpdates, { onConflict: 'id' })
    .select()
    .single()
    .then(({ data, error }) => {
      if (error) throw error
      return normalizeConfig(data)
    })

  return Promise.race([dbPromise, timeoutPromise])
}
